<template>
  <div class="my-4">
    <div v-for="item in model" :key="item.id" class="rounded p-3 bg-gray my-3">
      <div class="row">
        <div class="col-12 col-md">
          <div class="data-name">Tipo de Documento:</div>
          <div>{{ item.documentType.name | empty }}</div>
        </div>
        <div v-if="isItemOther(item.documentType.id)" class="col-12 col-md">
          <div class="data-name">Título del documento:</div>
          <div>{{ item.other | empty }}</div>
        </div>
        <div class="col-12 col-md">
          <div class="data-name">Archivo:</div>
          <div>{{ item.document.name | empty }}</div>
        </div>
        <div class="col-12 col-md">
          <div class="d-flex justify-content-end mt-1">
            <action-button
              v-if="!isReadOnly"
              i="fas fa-times-circle"
              variant="outline-danger mr-2"
              @click="remove(item)"
            />
            <action-button
              i="fas fa-file-download"
              variant="success"
              @click="download(item.document)"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="canAdd" class="rounded p-3 bg-gray my-3">
      <validation-observer
        ref="supportingDocumentsForm"
        v-slot="{ failed }"
        novalidate
      >
        <div class="row">
          <div class="col-12 col-md">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="innerModel.documentType"
                :class="{
                  'was-Invalidated-select': !!errors[0],
                }"
                custom-error-msg="El campo es requerido."
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                label="Tipo de Documento:"
                :options="documentTypes"
                placeholder="Seleccione"
              />
            </validation-provider>
          </div>
          <div v-if="isOther" class="col-12 col-md">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-input
                v-model="innerModel.other"
                custom-error-msg="El campo es requerido."
                :error="errors[0]"
                label="Nombre del Documento:"
              />
            </validation-provider>
          </div>
          <div
            class="col-12"
            :class="{ 'col-md-5': !isOther, 'col-md-4': isOther }"
          >
            <div class="data-name mb-2">
              Archivo:
            </div>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-file-input
                ref="fileUpload"
                v-model="innerModel.file"
                :accept="allowedFiles"
                custom-error-msg="El documento es requerido"
                display-style="normal"
                :error="errors[0]"
                :max-size="params.fileMaxSize"
                :on-download="() => {}"
              />
            </validation-provider>
          </div>
          <div
            class="col-12 col-md-3 d-flex justify-content-center align-items-center"
            :class="{
              'pt-4': !failed,
            }"
          >
            <action-button
              i="fas fa-times-circle"
              text="Cancelar"
              variant="outline-danger mr-2"
              @click="cancel"
            />
            <action-button
              i="fas fa-save"
              text="Guardar"
              variant="success"
              @click="save"
            />
          </div>
        </div>
      </validation-observer>
    </div>
    <div v-else-if="!isReadOnly" class="d-flex justify-content-end">
      <action-button
        i="fas fa-file-plus"
        text="Añadir"
        variant="success"
        @click="showAdd = true"
      />
    </div>
  </div>
</template>

<script>
import alertMixin from '@/mixins/alert.mixin.js';

import { ValidationProvider } from 'vee-validate';
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import CustomFileInput from 'CustomFileInput';
import ActionButton from 'ActionButton';

import generalApi from '@/modules/app/api';
import { getSysparameterByNames } from '@/modules/shared/sysparameter.api';
import {
  saveSupportingDocument,
  deleteSupportingDocument,
} from '@/modules/purchaseService/api.js';

import { serialize as objectToFormData } from 'object-to-formdata';
import DocumentTypes from '@/utils/constants/documentTypes';

const AddOtherParam = 'AllowAddOtherSupportingDocumentType';
const FileMaxParam = 'FileMaxSizeSupportingDocument';

export default {
  name: 'SupportingDocuments',
  components: {
    ValidationProvider,
    CustomInput,
    CustomDropDown,
    CustomFileInput,
    ActionButton,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Array,
      required: true,
      default: () => [],
    },
    documentTypes: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      innerModel: {},
      showAdd: false,
      params: {
        allowAddOther: false,
        fileMaxSize: 10,
      },
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    purchaseServiceId() {
      return Number(this.$route.params.id);
    },
    canAdd() {
      return !this.isReadOnly && (this.showAdd || !this.model.length);
    },
    allowedFiles() {
      return ['.pdf', '.docx', '.xlsx', '.png'].join(',');
    },
    isOther() {
      return (
        this.innerModel.documentType?.id ===
        DocumentTypes.OtherPurchaseServiceSupportingDocument
      );
    },
    isItemOther() {
      return (id) =>
        id === DocumentTypes.OtherPurchaseServiceSupportingDocument;
    },
    isDuplicateOtherTitle() {
      if (!this.isOther) return false;
      const title = this.innerModel.other.trim().toLowerCase();
      if (!title) return false;
      return this.model.some(
        (x) =>
          x.documentType?.id ===
            DocumentTypes.OtherPurchaseServiceSupportingDocument &&
          x.other.trim().toLowerCase() === title
      );
    },
  },
  async mounted() {
    await this.getParams();
  },
  methods: {
    async getParams() {
      const { data } = await getSysparameterByNames({
        parameterNames: [AddOtherParam, FileMaxParam],
      });
      if (!data?.length) return;
      this.params.allowAddOther =
        data
          .find((x) => x.name === AddOtherParam)
          ?.staticValue?.toLowerCase() == 'true';
      this.params.fileMaxSize = Number(
        data.find((x) => x.name === FileMaxParam)?.staticValue ?? 10
      );
    },
    clear() {
      this.innerModel = {};
      this.$refs.supportingDocumentsForm.reset();
      this.$refs.fileUpload.removeFile();
      this.cancel();
    },
    cancel() {
      this.showAdd = false;
    },
    async save() {
      const isValid = await this.$refs.supportingDocumentsForm.validate();
      if (!isValid) {
        this.ShowToast(
          '',
          'Debe indicar el tipo y seleccionar un archivo válido para continuar.',
          'error'
        );
        return;
      }

      if (this.isDuplicateOtherTitle) {
        this.ShowToast(
          '',
          'Ya existe un documento “Otro” con ese mismo título.',
          'error'
        );
        return;
      }

      try {
        const payload = objectToFormData(
          {
            purchaseServiceId: this.purchaseServiceId,
            ...this.innerModel,
            other: this.isOther ? this.innerModel.other : null,
          },
          {
            indices: true,
            nullsAsUndefineds: true,
          }
        );
        const { data } = await saveSupportingDocument(payload);
        this.model.push(data);
        this.clear();
        this.ShowToast('', 'Documento guardado exitosamente.', 'success');
      } catch {
        this.ShowToast(
          '',
          'Ocurrió un error al intentar guardar la información.',
          'error'
        );
      }
    },
    async remove({ id }) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea eliminar este documento adjunto?',
        'Si',
        'No'
      );

      if (!confirmed) return;
      try {
        await deleteSupportingDocument(id);
        this.ShowToast(
          '',
          'Documento adjunto eliminado exitosamente.',
          'success'
        );
        const index = this.model.findIndex((x) => x.id === id);
        this.model.splice(index, 1);
      } catch (error) {
        this.ShowToast('', error?.response?.data?.message, 'error');
      }
    },
    async download({ id }) {
      await generalApi.downloadDocumentByAttachmentId(id);
    },
  },
};
</script>
<style scoped>
div.was-Invalidated-select ::v-deep .v-select > div {
  background-color: #f7d7da;
  border: 1px solid #ff0018 !important;
}
::v-deep div.was-Invalidated {
  border: none !important;
}

::v-deep .was-Invalidated.file-input-container {
  border: 1px solid #ff0018 !important;
}
</style>
